import { resolveFormTenantId } from "../../utils/tenantContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { getStandardizedSignatures } from '../../utils/formHelpers';
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import { request } from "../../utils/api";
import { getLocalSystemDate, parseIsoDateToMDYUS } from "../../utils/dateUtils";
import { buildPayloadWithIsoDates } from "../../utils/applicationFormPayload";
import EvaluationFormHeader from "../EvaluationNew/components/EvaluationFormHeader";
import { hepatitisBPolicySchema } from "./application.validation";
import ApplicationFormFooter from './ApplicationFormFooter';
import CloseButton from "../../components/Application/CloseButton";
import CorrectionRequestAlert from "../../components/Application/CorrectionRequestAlert";
import SelectionGroup from "../../components/Application/SelectionGroup";

const HepatitisBPolicy = ({ positionName, onClose }) => {
  const [policyId, setPolicyId] = useState(null);



  const methods = useForm({
    resolver: zodResolver(hepatitisBPolicySchema),
    defaultValues: {
      tenantId: undefined,
      userId: undefined,
      employeeName: "",
      employeeTitle: "",
      isDecliningVaccination: false,
      hasBeenVaccinated: false,
      acknowledgementDate: parseIsoDateToMDYUS(getLocalSystemDate()),
      applicantSignature: "",
      applicantSignedAt: "",
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      reviewedBySignedBy: "",
      applicantSignedByUserId: 1,
      positionAppliedFor: positionName || ""
    },
    mode: "onChange"
  });

  // Integrated Form Logic & Signature Hook
  const [currentStatus, setCurrentStatus] = useState("");

  const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
    const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
    try {
      const payload = buildPayloadWithIsoDates(
        data,
        additionalData,
        ["acknowledgementDate", "applicantSignedAt", "reviewedBySignedAt"],
        { flag: actualFlag }
      );

      // Handle To Be Corrected comments
      const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
      if (finalComments) {
        payload.comments = finalComments;
      }

      if (policyId) {
        payload.policyId = policyId;
      }

      let response;
      if (policyId) {
        response = await request(`/hepatitis-b-policy/${policyId}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        response = await request('/hepatitis-b-policy', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }

      if (response.status === 1) {
        toast.success(response.message || "Hepatitis B Policy processed successfully!");
        closeForm();
      } else {
        toast.error(response.message || "Failed to process form!");
      }
    } catch (error) {
      console.error("Error processing form:", error);
      toast.error(`Failed to process form: ${error.message}`);
    }
  };

  const {
    handleStatusAction,
    handleSave,
    targetUserId,
    user: currentUserData,
    isAdmin,
    isReadOnly,
    closeForm,

    actualFromId,
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    handleSignatureApply,
    signerName
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "employeeName",
    applicantSignatureDateField: "applicantSignedAt",
    applicantSignatureField: "applicantSignature",
    adminSignatureField: "reviewedBySignature",
    adminSignatureDateField: "reviewedBySignedAt",
    onClose,
    onSubmit,
    currentStatus
  });

  const { handleSubmit, setValue, watch } = methods;

  useEffect(() => {
    if (positionName) {
      setValue("positionAppliedFor", positionName);
    }
  }, [positionName, setValue]);

  // Load Data Effect
  // Load Data Effect
  useEffect(() => {
    const fetchHepatitisBPolicy = async () => {
      // Must wait for currentUserData
      if (!currentUserData) return;
      if (isAdmin && !targetUserId && !policyId) return;

      try {
        if (!actualFromId && !targetUserId && !policyId) {
          if (currentUserData && !isAdmin) {
            methods.reset({
              tenantId: resolveFormTenantId(currentUserData?.tenantId),
              userId: currentUserData.id,
              employeeName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim(),
              positionAppliedFor: positionName || "",
              acknowledgementDate: parseIsoDateToMDYUS(getLocalSystemDate()),
              applicantSignedAt: "",

            });
          }
          return;
        }

        let response;
        if (isAdmin) {
          if (actualFromId) {
            response = await request(`/hepatitis-b-policy/${actualFromId}?userId=${targetUserId}`);
          } else {
            response = await request(`/hepatitis-b-policy?userId=${targetUserId}`);
          }
        } else {
          response = await request(`/hepatitis-b-policy/${actualFromId || targetUserId}`);
        }

        let data = null;
        if (response.status === 1) {
          if (Array.isArray(response.result)) {
            if (response.result.length > 0) {
              data = response.result[0];
            }
          } else if (response.result?.policy) {
            data = response.result.policy;
          } else if (response.result?.items && Array.isArray(response.result.items)) {
            if (response.result.items.length > 0) {
              data = response.result.items.find(item => item.userId === Number.parseInt(targetUserId, 10));
            }
          }
        }

        if (data) {
          setPolicyId(data.id);
          setCurrentStatus(data.status || "");
          methods.reset({
            ...data,
            employeeName: data.employeeName || data.applicantName || "",
            acknowledgementDate: parseIsoDateToMDYUS(data.acknowledgementDate || getLocalSystemDate()),
            ...getStandardizedSignatures(data, isAdmin),
            applicantSignedAt: parseIsoDateToMDYUS(getStandardizedSignatures(data, isAdmin).applicantSignedAt || getLocalSystemDate()),
            reviewedBySignedAt: parseIsoDateToMDYUS(getStandardizedSignatures(data, isAdmin).reviewedBySignedAt || ""),
          });
        }
      } catch (error) {
        console.error("Error fetching hepatitis B policy:", error);
      }
    };

    fetchHepatitisBPolicy();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load form when target user or record id changes
  }, [targetUserId, currentUserData, isAdmin, policyId, actualFromId, positionName]);


  return (
    <>
      <CloseButton onClose={closeForm} />
      <div className="max-w-5xl mx-auto bg-white shadow-xl rounded-2xl my-8">
        <EvaluationFormHeader title="Hepatitis B Vaccination Policy" />
        <div className="p-8">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <CorrectionRequestAlert status={currentStatus} comments={methods.watch("comments")} />
              <fieldset disabled={isReadOnly} className="space-y-6">
                <div className="mb-6 space-y-4 text-sm leading-relaxed text-gray-800">
                  <p>
                    The Center of Disease Control (CDC) and Occupational Safety and
                    Health Administration (OSHA) recommend immunization for all
                    healthcare workers in the high-risk category. As healthcare
                    personnel who will be exposed to the patients' blood and body
                    fluid, you will fall into this high-risk category.
                  </p>
                  <p>
                    The CDC Immunization Practices Advisory Committee recommends
                    that if you are NOT vaccinated, you should receive one dose of
                    Hepatitis Immune Globulin Human (H BIG) and begin a series of
                    Hepatitis B Virus (HBV) vaccine.
                  </p>
                </div>

                {/* Divider Line */}
                <div className="border-b-2 border-gray-400 my-6"></div>

                {/* Declaration Section */}
                <div className="mb-6 space-y-4 text-sm leading-relaxed text-gray-800">
                  <p>
                    I, the undersigned, have been offered the opportunity to receive
                    a Hepatitis B Vaccination, free of charge as an employment
                    benefit provided by our Agency.
                  </p>
                  <p>
                    I have read the above statement and I am aware that if
                    unvaccinated, I am at risk of contracting Hepatitis B during
                    employment.
                  </p>
                </div>

                {/* Vaccination Status Checkboxes */}
                <SelectionGroup
                  options={[
                    {
                      name: "isDecliningVaccination",
                      label: "I am declining to receive the vaccination at this time.",
                    },
                    {
                      name: "hasBeenVaccinated",
                      label: "I have already been vaccinated against the Hepatitis B virus.",
                    },
                  ]}
                  errorNames={["isDecliningVaccination", "hasBeenVaccinated"]}
                  isReadOnly={isReadOnly}
                  className="mb-8 p-6 bg-gray-50 rounded-xl border border-gray-100"
                />
                <ApplicationFormFooter
                  page={1}
                  setPage={() => { }}
                  totalPages={1}
                  methods={methods}
                  onSave={handleSave}
                  onSubmit={(data, status, extra) => onSubmit(data, status, extra)}
                  onStatusUpdate={handleStatusAction}
                  isSignatureModalOpen={isSignatureModalOpen}
                  openSignatureModal={openSignatureModal}
                  closeSignatureModal={closeSignatureModal}
                  handleSignatureApply={handleSignatureApply}
                  signerName={signerName}
                  showCertification={false}
                  requireSignatures={true}
                  showSignatureSections={true}
                  applicantName={watch("employeeName") || ""}
                  applicantSignature={watch("applicantSignature") || ""}
                  applicantSignedAt={watch("applicantSignedAt") || ""}
                  reviewedBySignedBy={watch("reviewedBySignedBy") || signerName}
                  reviewedBySignature={watch("reviewedBySignature") || ""}
                  reviewedBySignedAt={watch("reviewedBySignedAt") || ""}
                  submitLabel="Submit & Print"
                  isReadOnly={isReadOnly}
                  onClose={closeForm}
                  currentStatus={currentStatus}
                />

              </fieldset>
            </form>


          </FormProvider>
        </div>

      </div>
    </>
  );
};

export default HepatitisBPolicy;
